import {useClient, useDictionary} from '@adamlip/okeoke.client';
import {Card, CardBody, Drawer, DrawerBody, DrawerContent, DrawerHeader, ModalFooter, ScrollShadow} from '@heroui/react';
import SelectTableModal from 'components/Modals/SelectTableModal';
import {BasketContext} from 'contexts/BasketContext';
import useBasket from 'hooks/useBasket';
import {useContext, useState} from 'react';
import {AppParamsContext} from 'contexts/AppParamsContext';
import BarSoftButton from 'components/_NewComponents/BarSoftButton';
import LocationSelectorWithMap from 'components/Modals/SelectLocationModal/LocationSelectorWithMap';
import SendVibration from 'utils/Helpers-v1/SendVibration';
export default function SelectLocationModal({isOpen, setIsOpen, isDismissable}) {
	const {setSelectedLocation, locations, selectedLocationUUID} = useClient();
	const {appParams, desktopView} = useContext(AppParamsContext);
	const {setIsUserSelectedLocation} = useContext(BasketContext);

	const {getValue} = useDictionary();
	const {resetTable, savedTable} = useBasket();
	const [selectTableOpen, setSelectTableOpen] = useState(false);
	const [selectedLocalLocationUUID, setSelectedLocalLocationUUID] = useState(null);

	const handleLocationChange = (uuid, alsoClose) => {
		if (uuid !== savedTable?.locationUUID) {
			onClickResetTable();
		}
		setSelectedLocalLocationUUID(uuid);
		setIsUserSelectedLocation(true);
		SendVibration('light');

		if (alsoClose) {
			setIsOpen(false);
			setIsUserSelectedLocation(true);
			setSelectedLocation(uuid);
		}
	};

	const openSelectTableModal = () => {
		setSelectTableOpen(true);
	};

	const onClickResetTable = () => {
		resetTable();
	};

	const onSave = () => {
		setIsOpen(false);
		setIsUserSelectedLocation(true);
		setSelectedLocation(selectedLocalLocationUUID);
	};

	if (locations == null) return null;

	if (appParams?.styles?.global?.hideMap !== true) {
		return <LocationSelectorWithMap isOpen={isOpen} setIsOpen={setIsOpen} isDismissable={isDismissable} />;
	}
	return (
		<>
			<SelectTableModal isOpen={selectTableOpen} setIsOpen={setSelectTableOpen} onClose={() => setIsOpen(false)} />
			<Drawer
				isOpen={isOpen}
				isDismissable={isDismissable}
				onOpenChange={(open) => {
					if (isOpen && locations != null) {
						setIsOpen(true);
					}
				}}
				size='full'
				placement='bottom'
				hideCloseButton={true}
				classNames={{
					base: 'overflow-hidden flex flex-col max-h-full m-0 max-w-[820px] md:max-w-[500px] max-h-[900px]',
					closeButton: 'z-50 bg-primary text-primary-foreground',
				}}>
				<DrawerContent className={`${appParams?.styles?.fonts?.capitalize ? 'font-extrabold uppercase' : ''}`}>
					{(onClose) => (
						<>
							<DrawerHeader className='flex flex-col gap-1 w-full text-center'>{getValue('app_v2_selectLocation')}</DrawerHeader>
							<DrawerBody className='grow flex flex-col gap-2 overflow-hidden overflow-y-scroll px-2'>
								<div className='flex flex-col gap-2 w-full items-center justify-center'>
									{appParams?.order?.showTableSelector && (
										<div className='flex-row w-full flex gap-2 bg-secondaryBackground overflow-auto'>
											<BarSoftButton className='shrink-0 max-w-[200px]' fullWidth color='primary' onPress={openSelectTableModal}>
												{savedTable?.name == null && <div className='location-name select-table-text'>{getValue('app_v2_selectTable')}</div>}
												{savedTable?.name != null && <div className='location-address font-smallest'>{`${'kiválasztva: ' + savedTable?.name}`}</div>}
											</BarSoftButton>
											{savedTable?.name != null && (
												<BarSoftButton className='location-openState shrink-0' onPress={onClickResetTable}>
													{savedTable?.name != null && <div className={''}>{getValue('app_v2_resetTable')}</div>}
												</BarSoftButton>
											)}
										</div>
									)}
									<ScrollShadow className='w-full flex flex-col gap-2'>
										{Object.values(locations || {}).filter((location) => location.enabled === 1).length < 1 && <div className='font-bold text-waring'>{getValue('app_v2_no_locations')}</div>}
										{Object.values(locations || {})
											.filter((location) => location.enabled === 1)
											.map((location) => (
												<Card
													shadow='none'
													isPressable
													key={location.uuid}
													className={`${appParams?.styles?.fonts?.capitalize ? 'font-extrabold uppercase' : ''} shrink-0 gap-1 rounded-general ${location.uuid === selectedLocalLocationUUID ? ' border-primary bg-primary/20 text-primary' : 'border-border'} border-1 w-full`}
													onPress={() => {
														handleLocationChange(location.uuid);
													}}>
													<CardBody className='w-full flex flex-row gap-2 items-center justify-between'>
														<div className='text-current w-[calc(100%-83px)]'>
															<div className='font-bold'>{location.name}</div>
															<div className='text-xs'>{`${location.address?.city !== '' ? location.address?.city + ', ' : ''} ${location.address.zip} ${location.address.street} ${location.address.streetNumber}`}</div>
															{location?.openState?.forceClosedUntil != null && <div className='text-xs'>{`${getValue(`app_v2_location_pause_reason_${location?.openState?.forceClosedUntilReason}`)} ${getValue('app_v2_location_pause_text')} ${location?.openState?.forceClosedUntil}`}</div>}
														</div>
														<div className='flex flex-row gap-2 items-center'>
															{location?.openState?.forceClosedUntil != null && (
																<BarSoftButton color='warning' className='uppercase'>
																	{getValue('app_v2_location_pause')}
																</BarSoftButton>
															)}
															{location?.openState?.forceClosedUntil == null && (
																<div className={`uppercase p-1 rounded-lg text-sm ${location?.openState?.open ? 'bg-success text-success-foreground' : 'bg-danger text-danger-foreground'}`}>{location?.openState?.open ? getValue('app_v2_location_open') : getValue('app_v2_location_closed')}</div>
															)}
														</div>
													</CardBody>
												</Card>
											))}
									</ScrollShadow>
								</div>
							</DrawerBody>
							<ModalFooter className='flex items-center justify-center'>
								{isDismissable && (
									<BarSoftButton color='danger' variant='light' onPress={() => setIsOpen(false)} className={`${appParams?.styles?.fonts?.capitalize ? 'font-extrabold uppercase' : ''}`}>
										{getValue('app_v2_locationSelector_cancel')}
									</BarSoftButton>
								)}
								<BarSoftButton color='primary' onPress={() => onSave()} isDisabled={selectedLocalLocationUUID == null} className={`${appParams?.styles?.fonts?.capitalize ? 'font-extrabold uppercase' : ''}`} fullWidth>
									{`${locations?.[selectedLocalLocationUUID]?.name || getValue('app_v2_select_location')}`}
								</BarSoftButton>
							</ModalFooter>
						</>
					)}
				</DrawerContent>
			</Drawer>
		</>
	);
}
